import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import type { InsuranceCompanyDto } from '../types/insurance';

interface InsuranceCompanyFormProps {
  company?: InsuranceCompanyDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: InsuranceCompanyFormData) => Promise<void>;
  isSubmitting?: boolean;
  error?: string | null;
}

export interface InsuranceCompanyFormData {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  isActive: boolean;
}

function InsuranceCompanyFormInner({
  initialValues,
  isEditMode,
  onSubmit,
  onCancel,
  isSubmitting = false,
  error,
}: {
  initialValues: InsuranceCompanyFormData;
  isEditMode: boolean;
  onSubmit: (data: InsuranceCompanyFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
}) {
  const [values, setValues] = useState<InsuranceCompanyFormData>(initialValues);
  const [validationError, setValidationError] = useState('');

  const handleChange = (field: keyof InsuranceCompanyFormData, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    const trimmedName = values.name.trim();
    if (!trimmedName) {
      setValidationError('Company name is required');
      return;
    }

    if (trimmedName.length > 200) {
      setValidationError('Company name must be 200 characters or less');
      return;
    }

    try {
      await onSubmit({ ...values, name: trimmedName });
    } catch {
      // Error handled by parent
    }
  };

  const displayError = validationError || error;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <ErrorAlert error={displayError} />

      <div className="space-y-2">
        <Label htmlFor="ic-name">Company Name *</Label>
        <Input
          id="ic-name"
          value={values.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="Enter company name"
          disabled={isSubmitting}
          autoFocus
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="ic-address1">Address Line 1</Label>
          <Input
            id="ic-address1"
            value={values.address1}
            onChange={(e) => handleChange('address1', e.target.value)}
            placeholder="Street address"
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="ic-address2">Address Line 2</Label>
          <Input
            id="ic-address2"
            value={values.address2}
            onChange={(e) => handleChange('address2', e.target.value)}
            placeholder="Suite, unit, etc."
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ic-city">City</Label>
          <Input
            id="ic-city"
            value={values.city}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
            disabled={isSubmitting}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ic-state">State</Label>
            <Input
              id="ic-state"
              value={values.state}
              onChange={(e) => handleChange('state', e.target.value)}
              placeholder="ST"
              maxLength={2}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ic-zip">Zip</Label>
            <Input
              id="ic-zip"
              value={values.zip}
              onChange={(e) => handleChange('zip', e.target.value)}
              placeholder="Zip code"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ic-phone">Phone</Label>
          <Input
            id="ic-phone"
            value={values.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="Phone number"
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ic-email">Email</Label>
          <Input
            id="ic-email"
            type="email"
            value={values.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="Email address"
            disabled={isSubmitting}
          />
        </div>
      </div>

      {isEditMode && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="ic-active"
            checked={values.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            disabled={isSubmitting}
          />
          <Label htmlFor="ic-active" className="font-normal cursor-pointer">
            Active
          </Label>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Company'}
        </Button>
      </div>
    </form>
  );
}

export function InsuranceCompanyForm({
  company,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  error,
}: InsuranceCompanyFormProps) {
  const isEditMode = !!company;
  const formKey = company?.id ?? 'new';

  const initialValues: InsuranceCompanyFormData = {
    name: company?.name ?? '',
    address1: company?.address1 ?? '',
    address2: company?.address2 ?? '',
    city: company?.city ?? '',
    state: company?.state ?? '',
    zip: company?.zip ?? '',
    phone: company?.phone ?? '',
    email: company?.email ?? '',
    isActive: company?.isActive ?? true,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Insurance Company' : 'Add Insurance Company'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the insurance company details below.'
              : 'Enter the details for the new insurance company.'}
          </DialogDescription>
        </DialogHeader>
        <InsuranceCompanyFormInner
          key={formKey}
          initialValues={initialValues}
          isEditMode={isEditMode}
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
          isSubmitting={isSubmitting}
          error={error}
        />
      </DialogContent>
    </Dialog>
  );
}
